import React, { useCallback, lazy, Suspense, useState, useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import superjson from 'superjson';
import Sidebar from './components/Sidebar';
import Player from './components/Player';
import { ErrorBoundary } from './components/ErrorBoundary';
import AIDisclosureModal from './components/AIDisclosureModal';
import { IOSInstallPrompt } from './components/IOSInstallPrompt';
import { Toaster } from 'sonner';
import {
  PlayerProvider,
  usePlayer,
  usePlayerActions,
} from './contexts/PlayerContext';
import {
  SettingsProvider,
  useSettingsContext,
} from './contexts/SettingsContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ZonesProvider } from './contexts/ZonesContext';
import { useUIStore } from './stores/uiStore';
import { capture } from './lib/posthog';
import { trpc } from './lib/trpc';
import { createUserPlaylist } from './hooks/useUserPlaylists';
import { runMigrationV2 } from './lib/migrations/v2-playlists';
import { MigrationBanner } from './components/MigrationBanner';
import { PlayerTour } from './components/tours/PlayerTour';
import './App.css';

const SearchModal = lazy(() =>
  import('./components/SearchModal').then((m) => ({ default: m.SearchModal }))
);
const QueueDrawer = lazy(() =>
  import('./components/QueueDrawer').then((m) => ({ default: m.QueueDrawer }))
);
const Onboarding = lazy(() =>
  import('./components/Onboarding').then((m) => ({ default: m.Onboarding }))
);
const HomeView = lazy(() => import('./components/HomeView'));
const BrowseView = lazy(() => import('./components/BrowseView'));
const BrowsePlaylistsView = lazy(
  () => import('./components/BrowsePlaylistsView')
);
const PlaylistView = lazy(() => import('./components/PlaylistView'));
const ScheduleView = lazy(() => import('./components/ScheduleView'));
const CertificateView = lazy(() => import('./components/CertificateView'));
const KioskView = lazy(() =>
  import('./components/KioskView').then((m) => ({ default: m.KioskView }))
);
const LibraryView = lazy(() =>
  import('./components/LibraryView').then((m) => ({ default: m.LibraryView }))
);
const NowPlayingView = lazy(() =>
  import('./components/NowPlayingView').then((m) => ({
    default: m.NowPlayingView,
  }))
);
const SettingsView = lazy(() =>
  import('./components/SettingsView').then((m) => ({ default: m.SettingsView }))
);
const EmployeeView = lazy(() =>
  import('./components/EmployeeView').then((m) => ({ default: m.EmployeeView }))
);
const ExplorerView = lazy(() => import('./components/ExplorerView'));
const PlansView = lazy(() => import('./components/PlansView'));
const SAVChat = lazy(() => import('./components/SAVChat'));
const ZonesView = lazy(() => import('./components/ZonesView'));
const NotFoundView = lazy(() => import('./components/NotFoundView'));
const LoginView = lazy(() => import('./components/LoginView'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const SAVChatWidget = lazy(() => import('./components/SAVChatWidget'));

function TrpcProvider({ children }) {
  const { session } = useAuth();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { staleTime: 30 * 1000, retry: 1 } },
      })
  );
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url:
            (import.meta.env.VITE_API_URL || 'http://localhost:8787') +
            '/api/trpc',
          transformer: superjson,
          headers: () =>
            session?.access_token
              ? { Authorization: `Bearer ${session.access_token}` }
              : {},
        }),
      ],
    })
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div>Chargement...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const RouteSkeleton = () => (
  <div className="route-skeleton" style={{ padding: 24 }}>
    <div
      className="skel skel-line"
      style={{ width: '60%', height: 32, marginBottom: 16 }}
    />
    <div
      className="skel skel-line"
      style={{ width: '90%', height: 16, marginBottom: 8 }}
    />
    <div className="skel skel-line" style={{ width: '80%', height: 16 }} />
  </div>
);

function AppLayout({ children }) {
  const {
    sidebarOpen,
    setSidebarOpen,
    closeSidebar,
    currentPlaylist,
    setCurrentPlaylist,
  } = useUIStore();

  return (
    <div className="app">
      <a href="#main" className="skip-link">
        Aller au contenu principal
      </a>
      <div className="mobile-header">
        <button
          className="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={
            sidebarOpen ? 'Fermer la navigation' : 'Ouvrir la navigation'
          }
          aria-expanded={sidebarOpen}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <span className="mobile-logo">
          melo<span className="accent">diza</span>
        </span>
      </div>

      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} />
      )}

      <div className={`sidebar-wrapper ${sidebarOpen ? 'open' : ''}`}>
        <Sidebar
          currentPlaylist={currentPlaylist}
          setCurrentPlaylist={setCurrentPlaylist}
          onNavClick={closeSidebar}
        />
      </div>

      <main id="main" className="main-content">
        <MigrationBanner />
        <Suspense fallback={<RouteSkeleton />}>{children}</Suspense>
      </main>
    </div>
  );
}

function PlayerSlot() {
  const {
    currentTrack,
    isPlaying,
    playlist,
    currentTrackIndex,
    setIsPlaying,
    setTrackIndex,
    handleTimeUpdate,
    registerAudioRef,
    setVolume,
    volume,
  } = usePlayer();
  const { openFullscreen, openQueue, closeQueue, isQueueOpen } = useUIStore();

  return (
    <Player
      currentTrack={currentTrack}
      isPlaying={isPlaying}
      isQueueOpen={isQueueOpen}
      setIsPlaying={setIsPlaying}
      playlist={playlist}
      setCurrentTrackIndex={setTrackIndex}
      currentTrackIndex={currentTrackIndex}
      onOpenFullscreen={openFullscreen}
      onTimeUpdate={handleTimeUpdate}
      onAudioRef={registerAudioRef}
      volumeExternal={volume}
      onVolumeChange={setVolume}
      onOpenQueue={() => {
        if (isQueueOpen) closeQueue();
        else openQueue();
      }}
    />
  );
}

function OverlaysSlot() {
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    playTrack,
    setIsPlaying,
    setTrackIndex,
    currentTrackIndex,
    playlist,
    seek,
    setVolume,
    isLiked,
    toggleLike,
    queue,
    setQueue,
    removeFromQueue,
    clearQueue,
    recentlyPlayed,
  } = usePlayer();
  const {
    searchOpen,
    closeSearch,
    isFullscreen,
    closeFullscreen,
    isQueueOpen,
    closeQueue,
    setCurrentPlaylist,
    openQueue,
  } = useUIStore();
  const navigate = useNavigate();

  // Phase 3 Step 0 — defer-until-interaction pattern.
  // NowPlayingView and QueueDrawer chunks (motion ~39kB gz, dnd ~14kB gz) are
  // deferred until the user first opens them. Without this gate, Vite modulepreload
  // adds these chunks to <link rel="modulepreload"> in index.html at boot time,
  // increasing initial JS parse cost by ~53kB gz and degrading Lighthouse TBT.
  const [hasOpenedFullscreen, setHasOpenedFullscreen] = useState(false);
  const [hasOpenedQueue, setHasOpenedQueue] = useState(false);

  // Sync: if isFullscreen / isQueueOpen becomes true (e.g. from Player.js button click
  // that calls openFullscreen() directly), ensure the gate opens so the lazy chunk loads.
  React.useEffect(() => {
    if (isFullscreen) setHasOpenedFullscreen(true);
  }, [isFullscreen]);

  React.useEffect(() => {
    if (isQueueOpen) setHasOpenedQueue(true);
  }, [isQueueOpen]);

  const onSelectPlaylist = useCallback(
    (pl) => {
      setCurrentPlaylist(pl);
      navigate(`/playlist/${pl.id}`);
    },
    [navigate, setCurrentPlaylist]
  );

  return (
    <>
      <Suspense fallback={null}>
        <SearchModal
          open={searchOpen}
          onClose={closeSearch}
          onSelectTrack={(track) => playTrack(track)}
          onSelectPlaylist={onSelectPlaylist}
          onSelectGenre={(g) =>
            navigate(
              `/explorer?genre=${encodeURIComponent(typeof g === 'string' ? g : g.id || g.name)}`
            )
          }
        />
      </Suspense>
      {hasOpenedFullscreen && (
        <Suspense fallback={null}>
          <NowPlayingView
            open={isFullscreen}
            onClose={closeFullscreen}
            currentTrack={currentTrack}
            isPlaying={isPlaying}
            onPlayPause={() => setIsPlaying((prev) => !prev)}
            onPrev={() => setTrackIndex(Math.max(0, currentTrackIndex - 1))}
            onNext={() =>
              setTrackIndex(
                Math.min((playlist.length || 1) - 1, currentTrackIndex + 1)
              )
            }
            isLiked={isLiked(currentTrack?.id)}
            onLike={toggleLike}
            currentTime={currentTime}
            duration={duration}
            onSeek={seek}
            volume={volume}
            onVolumeChange={setVolume}
            onOpenQueue={openQueue}
          />
        </Suspense>
      )}
      {hasOpenedQueue && (
        <Suspense fallback={null}>
          <QueueDrawer
            open={isQueueOpen}
            onClose={closeQueue}
            currentTrack={currentTrack}
            queue={queue}
            upcomingFromPlaylist={
              playlist && playlist.length > currentTrackIndex + 1
                ? playlist.slice(currentTrackIndex + 1)
                : []
            }
            onReorder={setQueue}
            onRemove={removeFromQueue}
            onClearQueue={clearQueue}
            onPlayFromQueue={(track, index) => {
              // Play this track immediately + remove already-played items from queue
              const remaining = queue.slice(index);
              playTrack(track, remaining, 0);
              setQueue(remaining.slice(1));
            }}
            onPlayFromPlaylist={(track, index) => {
              // Jump to that index in the current playlist
              setTrackIndex(currentTrackIndex + 1 + index);
            }}
            recentlyPlayed={recentlyPlayed}
          />
        </Suspense>
      )}
    </>
  );
}

function AppKeyboardShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsPlaying, setVolume } = usePlayerActions();
  const { setSearchOpen } = useUIStore();

  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
        return;
      }
      const tag = document.activeElement.tagName;
      const isEditable =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        document.activeElement.isContentEditable;
      if (e.key === 'k' && !e.metaKey && !e.ctrlKey && !isEditable) {
        navigate(location.pathname === '/kiosk' ? '/' : '/kiosk');
        return;
      }
      if (e.key === 'Escape' && !isEditable) {
        if (location.pathname === '/kiosk') navigate('/');
        return;
      }
      if (
        e.key === ' ' &&
        !isEditable &&
        document.activeElement.tagName !== 'BUTTON'
      ) {
        e.preventDefault();
        setIsPlaying((prev) => !prev);
        return;
      }
      if (e.key === 'm' && !isEditable) {
        setVolume((prev) => (prev === 0 ? 0.7 : 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, location.pathname, setIsPlaying, setVolume, setSearchOpen]);

  return null;
}

function AppRoutes() {
  const {
    playTrack,
    currentTrack,
    isPlaying,
    setIsPlaying,
    setTrackIndex,
    currentTrackIndex,
    setVolume,
    volume,
    isLiked,
    toggleLike,
    currentTime,
    duration,
    seek,
  } = usePlayer();
  const { setCurrentPlaylist } = useUIStore();
  const {
    showOnboarding,
    setShowOnboarding,
    setUserPrefs,
    hasAcknowledgedAIDisclosure,
    acknowledgeAIDisclosure,
  } = useSettingsContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { setShowMigrationBanner } = useUIStore();

  // A.3 — Migration v2: auto-seed liked playlists for existing early-access users
  useEffect(() => {
    const result = runMigrationV2();
    if (result.requiresBanner) {
      setShowMigrationBanner(true);
    }
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: intentional partial deps (run once on mount)
  }, []);

  const onSelectPlaylist = useCallback(
    (pl) => {
      setCurrentPlaylist(pl);
      capture('playlist_played', {
        playlistId: pl.id,
        name: pl.name,
        trackCount: pl.tracks?.length ?? 0,
        source: 'manual',
      });
      navigate(`/playlist/${pl.id}`);
    },
    [navigate, setCurrentPlaylist]
  );

  const { currentPlaylist } = useUIStore();

  return (
    <>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="track-announcer"
      >
        {currentTrack &&
          `Maintenant : ${currentTrack.title} par ${currentTrack.artist} — ${currentTrack.bpm} BPM`}
      </div>

      <AppKeyboardShortcuts />

      {/* Tours removed entirely per user feedback — Onboarding wizard handles first-action UX */}

      <Routes>
        {/* Public routes — no sidebar layout, no auth required */}
        <Route
          path="/login"
          element={
            <Suspense fallback={<RouteSkeleton />}>
              <LoginView />
            </Suspense>
          }
        />
        <Route
          path="/auth/callback"
          element={
            <Suspense fallback={<RouteSkeleton />}>
              <AuthCallback />
            </Suspense>
          }
        />

        {/* Kiosk — layoutless fullscreen route */}
        <Route
          path="/kiosk"
          element={
            <Suspense fallback={<RouteSkeleton />}>
              <KioskView
                currentTrack={currentTrack}
                isPlaying={isPlaying}
                onExit={() => navigate('/')}
              />
            </Suspense>
          }
        />

        {/* All other routes — layout with sidebar, protected */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <HomeView
                        onSelectPlaylist={onSelectPlaylist}
                        onPlayTrack={playTrack}
                      />
                    }
                  />
                  <Route
                    path="/explorer"
                    element={<ExplorerView onPlayTrack={playTrack} />}
                  />
                  <Route
                    path="/browse"
                    element={<BrowseView onPlayTrack={playTrack} />}
                  />
                  <Route
                    path="/playlists"
                    element={
                      <BrowsePlaylistsView
                        onSelectPlaylist={onSelectPlaylist}
                        onPlayTrack={playTrack}
                      />
                    }
                  />
                  <Route
                    path="/playlist/:id"
                    element={
                      <PlaylistView
                        playlist={currentPlaylist}
                        onPlayTrack={playTrack}
                      />
                    }
                  />
                  <Route path="/schedule" element={<ScheduleView />} />
                  <Route path="/certificate" element={<CertificateView />} />
                  <Route
                    path="/library"
                    element={
                      <LibraryView
                        onSelectTrack={(track) => playTrack(track)}
                        onNavigate={(path) => navigate(path)}
                        currentTrackId={currentTrack?.id}
                        isPlaying={isPlaying}
                      />
                    }
                  />
                  <Route
                    path="/library/:tab"
                    element={
                      <LibraryView
                        onSelectTrack={(track) => playTrack(track)}
                        onNavigate={(path) => navigate(path)}
                        currentTrackId={currentTrack?.id}
                        isPlaying={isPlaying}
                      />
                    }
                  />
                  <Route path="/settings" element={<SettingsView />} />
                  <Route
                    path="/employee"
                    element={
                      <EmployeeView
                        currentTrack={currentTrack}
                        isPlaying={isPlaying}
                        onPlayPause={() => setIsPlaying((prev) => !prev)}
                        onNext={() => setTrackIndex(currentTrackIndex + 1)}
                        onPrev={
                          currentTrackIndex > 0
                            ? () => setTrackIndex(currentTrackIndex - 1)
                            : undefined
                        }
                        onLike={
                          currentTrack
                            ? () => toggleLike(currentTrack.id)
                            : undefined
                        }
                        isLiked={
                          currentTrack ? isLiked(currentTrack.id) : false
                        }
                        currentTime={currentTime}
                        duration={duration}
                        onSeek={seek}
                        volume={volume}
                        onVolumeChange={setVolume}
                        onSwitchAmbiance={() => navigate('/explorer')}
                      />
                    }
                  />
                  <Route path="/plans" element={<PlansView />} />
                  <Route path="/sav" element={<SAVChat />} />
                  <Route path="/zones" element={<ZonesView />} />
                  <Route path="*" element={<NotFoundView />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>

      {(() => {
        const isPublicRoute =
          location.pathname.startsWith('/login') ||
          location.pathname.startsWith('/auth/');
        const hidePlayer = isPublicRoute || location.pathname === '/kiosk';
        return (
          <>
            <div style={hidePlayer ? { display: 'none' } : undefined}>
              <PlayerSlot />
            </div>
            {!isPublicRoute && <OverlaysSlot />}
            {!isPublicRoute && user && (
              <Suspense fallback={null}>
                <SAVChatWidget />
              </Suspense>
            )}
          </>
        );
      })()}

      <AIDisclosureModal
        open={!hasAcknowledgedAIDisclosure}
        onAcknowledge={acknowledgeAIDisclosure}
      />

      {!location.pathname.startsWith('/login') &&
        !location.pathname.startsWith('/auth/') &&
        hasAcknowledgedAIDisclosure &&
        showOnboarding && (
          <Suspense fallback={null}>
            <Onboarding
              onComplete={(prefs) => {
                setUserPrefs(prefs);
                setShowOnboarding(false);
                if (prefs?.playlist?.length) {
                  playTrack(prefs.playlist[0], prefs.playlist, 0);
                  const businessLabel =
                    {
                      restaurant: 'Restaurant',
                      bar: 'Bar',
                      salon: 'Salon',
                      boutique: 'Boutique',
                      spa: 'Spa',
                      gym: 'Salle de sport',
                      hotel: 'Hôtel',
                      office: 'Bureau',
                    }[prefs.businessType] || 'Mon espace';
                  const newPl = createUserPlaylist(
                    `Ma sélection ${businessLabel}`,
                    prefs.playlist.map((t) => t.id)
                  );
                  capture('onboarding_playlist_saved', {
                    playlistId: newPl.id,
                    trackCount: newPl.trackIds.length,
                    businessType: prefs.businessType,
                  });
                }
                useUIStore.getState().setStartPlayerTour(true);
              }}
            />
          </Suspense>
        )}
      {!location.pathname.startsWith('/login') &&
        !location.pathname.startsWith('/auth/') &&
        hasAcknowledgedAIDisclosure && <PlayerTour />}
      <Toaster
        position="bottom-center"
        richColors
        closeButton
        expand
        visibleToasts={3}
        offset={{ bottom: '100px' }}
      />
      <IOSInstallPrompt />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <SettingsProvider>
        <AuthProvider>
          <TrpcProvider>
            <ZonesProvider>
              <PlayerProvider>
                <AppRoutes />
              </PlayerProvider>
            </ZonesProvider>
          </TrpcProvider>
        </AuthProvider>
      </SettingsProvider>
    </ErrorBoundary>
  );
}

export default App;
